import React, { useMemo, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { FaAngleLeft, FaAngleRight, FaTimes } from 'react-icons/fa';

// =========================
// Types
// =========================
export type AlbumImage = {
    src: string;
    alt?: string;
    width?: number;
    height?: number;
};

export type Album = {
    id: string;
    title: string;
    year: number;
    coverUrl: string;
    count?: number;
    images: AlbumImage[];
};

export type AlbumsGridProps = {
    /** API endpoint that returns the raw albums (your /api/v1/albums) */
    apiUrl: string;
    /** Optional extra slugs to exclude, merged with the built‑ins */
    excludeSlugs?: string[];
    className?: string;
};

// =========================
// Small utils
// =========================
const cn = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(" ");

const getRatio = (img?: AlbumImage) => {
    if (!img?.width || !img?.height) return 3 / 2; // default 3:2
    return img.width / img.height;
};

function groupByYear(albums: Album[]) {
    const map = new Map<number, Album[]>();
    for (const a of albums) {
        const list = map.get(a.year) ?? [];
        list.push(a);
        map.set(a.year, list);
    }
    const years = Array.from(map.keys()).sort((a, b) => b - a);
    return years.map((year) => ({
        year,
        albums: map.get(year)!.slice().sort((a, b) => Number(b.id) - Number(a.id)),
    }));
}

// Tailwind primitives (no shadcn)
const Badge = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-flex items-center rounded-md bg-white/90 px-2 py-1 text-xs font-medium text-gray-900 shadow-sm backdrop-blur">
    {children}
  </span>
);

const Card = ({ className, onClick, children }: React.PropsWithChildren<{ className?: string; onClick?: () => void }>) => (
    <div onClick={onClick} className={cn("rounded-2xl bg-white shadow-sm ring-1 ring-black/5", className)}>
        {children}
    </div>
);

function Modal({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative z-10 w-full max-w-5xl overflow-hidden rounded-2xl bg-black">{children}</div>
        </div>
    );
}

function YearAccordion({ year, count, defaultOpen, children }: { year: number; count: number; defaultOpen?: boolean; children: React.ReactNode }) {
    return (
        <details className="m-14 group rounded-xl border border-black bg-white shadow-sm" {...(defaultOpen ? { open: true } : {})}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
                <span className="text-xl sm:text-2xl font-semibold">{year}</span>
                <span className="text-sm text-gray-500">{count} album{count !== 1 ? "s" : ""}</span>
            </summary>
            <div className="px-4 pb-4">{children}</div>
        </details>
    );
}

// =========================
// API Shapes (backend)
// =========================
// These reflect your /api/v1/albums response
export type ApiImage = {
    id: number;
    album_id: number;
    filename: string;
    file_path: string;
    width: number;
    height: number;
    size: number;
    mime_type: string;
};

export type ApiAlbum = {
    id: number;
    title: string;
    slug: string;
    folder: string;
    images: ApiImage[];
    createdAt?: string;
    year?: number;
};

// =========================
// Main Component
// =========================
export default function AlbumsGrid({ apiUrl, excludeSlugs, className }: AlbumsGridProps) {
    const [albums, setAlbums] = useState<Album[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [activeAlbum, setActiveAlbum] = useState<Album | null>(null);
    const [index, setIndex] = useState(0);

    // Built-in exclusions + user provided
    const EXCLUDED = useMemo(() => new Set(["posts", "luchtdoelschieten", ...(excludeSlugs ?? [])]), [excludeSlugs]);

    const toPublicUrl = useCallback((file_path: string) => {
        const clean = file_path.replace(/^\/?public_html/, ""); // => /albums/...

        return clean;
    }, []);

    const mapApiAlbum = useCallback((api: ApiAlbum): Album => {
        const images: AlbumImage[] = (api.images || []).map((img) => ({
            src: toPublicUrl(img.file_path),
            width: img.width,
            height: img.height,
            alt: img.filename,
        }));

        const coverUrl = "https://cdn.robinhoodboechout.be" + images[0]?.src;
        const year = api.year ?? (api.createdAt ? new Date(api.createdAt).getFullYear() : new Date().getFullYear());

        return {
            id: String(api.id),
            title: api.title,
            year,
            coverUrl,
            images,
            count: images.length,
        };
    }, [toPublicUrl]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                const res = await fetch("https://api.robinhoodboechout.be" + apiUrl);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: ApiAlbum[] = await res.json();
                const filtered = data.filter((a) => !EXCLUDED.has(a.slug));
                const mapped = filtered.map(mapApiAlbum);
                if (mounted) setAlbums(mapped);
            } catch (e: any) {
                if (mounted) setError(e.message || "Failed to load albums");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [apiUrl, EXCLUDED, mapApiAlbum]);

    const handleOpen = useCallback((album: Album) => {
        setActiveAlbum(album);
        setIndex(0);
        setOpen(true);
    }, []);

    const close = () => setOpen(false);
    const next = useCallback(() => activeAlbum && setIndex((i) => (i + 1) % activeAlbum.images.length), [activeAlbum]);
    const prev = useCallback(() => activeAlbum && setIndex((i) => (i - 1 + activeAlbum.images.length) % activeAlbum.images.length), [activeAlbum]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!open) return;
            if (e.key === "Escape") close();
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, next, prev]);

    const grouped = useMemo(() => groupByYear(albums), [albums]);
    const currentImage = activeAlbum?.images[index];
    const ratio = getRatio(currentImage);

    if (loading) return <div className={cn("p-6 text-sm text-gray-500", className)}>Foto's aan het laden…</div>;
    if (error) return <div className={cn("p-6 text-sm text-red-600", className)}>Error: {error}</div>;

    return (
        <div className={cn("space-y-6", className)}>
            {/* Collapsible sections per year */}
            <div className="space-y-4 mb-14">
                {grouped.map(({ year, albums }, i) => (
                    <YearAccordion key={year} year={year} count={albums.length} defaultOpen={i === 0}>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {albums.map((album) => (
                                <motion.div key={album.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                                    <Card onClick={() => handleOpen(album)} className="group cursor-pointer overflow-hidden ring-1 ring-black/5 transition-shadow hover:shadow-md">
                                        <div className="relative">
                                            <img src={album.coverUrl} alt={`Cover of ${album.title}`} className="h-56 w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" loading="lazy" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                            <div className="absolute inset-x-3 bottom-3 flex items-end justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="truncate text-white text-base sm:text-lg font-medium drop-shadow">{album.title}</div>
                                                </div>
                                                <Badge>
                                                    {album.count ?? album.images.length}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    </YearAccordion>

                ))}
            </div>

            {/* Lightbox */}
            <Modal open={open} onClose={close}>
                <button aria-label="Close gallery" className="absolute right-3 top-3 z-20 rounded-full bg-white/90 p-2 text-gray-900 hover:bg-white" onClick={close}>
                    <FaTimes className="h-5 w-5" />
                </button>
                <div className="relative flex items-center justify-center" style={{ aspectRatio: `${ratio}` }}>
                    {currentImage && (
                        <img key={currentImage.src} src={"https://cdn.robinhoodboechout.be" + currentImage.src} alt={currentImage.alt ?? "Photo"} className="max-h-[80vh] w-auto object-contain" />
                    )}
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-between p-2">
                        <button className="pointer-events-auto rounded-full bg-white/90 px-3 py-2 text-gray-900 hover:bg-white" onClick={prev} aria-label="Previous">
                            <FaAngleLeft className="h-5 w-5" />
                        </button>
                        <button className="pointer-events-auto rounded-full bg-white/90 px-3 py-2 text-gray-900 hover:bg-white" onClick={next} aria-label="Next">
                            <FaAngleRight  className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                <div className="flex gap-2 overflow-x-auto bg-black/70 p-3">
                    {activeAlbum?.images.map((img, i) => (
                        <button key={img.src} onClick={() => setIndex(i)} className={cn("relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border", i === index ? "ring-2 ring-white border-white/60" : "border-white/20")} aria-label={`Go to image ${i + 1}`}>
                            <img src={"https://cdn.robinhoodboechout.be" + img.src} alt={img.alt ?? `Thumbnail ${i + 1}`} className="h-full w-full object-cover" />
                        </button>
                    ))}
                </div>
            </Modal>
        </div>
    );
}